import { LeaveDuration } from '@gokrew/models/leaves';

/** The calendar payload (company and employee portals alike) is keyed by
 *  date — the backend expands a multi-day request into one entry per working
 *  day it covers — so a single day's entry is only ever hourly, half-day, or
 *  full-day; there is no multi-day kind to classify here. */
export type OnLeaveDurationKind = Exclude<
  LeaveDuration,
  LeaveDuration.MULTI_DAY
>;

interface DurationShape {
  startTime?: string | null;
  endTime?: string | null;
  startSession?: string | null;
  endSession?: string | null;
}

/** Classifies a single day's leave entry as hourly / half-day / full-day.
 *  Shared by the company portal's OnLeave card and the employee portal's
 *  OnLeaveTodayCard, which read the exact same `CalendarLeave` shape. */
export function getOnLeaveDurationKind(
  leave: DurationShape
): OnLeaveDurationKind {
  if (leave.startTime && leave.endTime) return LeaveDuration.HOURLY;
  if (leave.startSession && leave.startSession === leave.endSession) {
    return LeaveDuration.HALF_DAY;
  }
  return LeaveDuration.FULL_DAY;
}

/**
 * Builds the "2 past + today + N future working days" window both On Leave
 * cards show as a day-strip, skipping weekly-off days. Returns `Date`
 * objects — each caller formats `today`'s key with its own portal's date
 * utility (`toLocalDateStr` vs `formatDate`).
 */
export function getWorkingDayWindow(
  weeklyHolidays: number[],
  config: { past?: number; future?: number; maxScan?: number } = {}
) {
  const { past = 2, future = 4, maxScan = 30 } = config;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const walk = (direction: 1 | -1, wanted: number) => {
    const found: Date[] = [];
    const cursor = new Date(today);

    for (let scanned = 0; found.length < wanted; scanned += 1) {
      if (scanned >= maxScan) break;
      cursor.setDate(cursor.getDate() + direction);
      if (!weeklyHolidays.includes(cursor.getDay())) {
        found.push(new Date(cursor));
      }
    }

    return found;
  };

  return {
    today,
    dates: [...walk(-1, past).reverse(), today, ...walk(1, future)],
  };
}

/** A holiday as the leave calendar payloads carry it (company calendar and
 *  employee on-leave v2). `type`, `appliesToAllBranches` and `branchIds` are
 *  optional so an older payload still reads as an all-branch day off. */
export interface OnLeaveHolidayLike {
  /** The day off, `YYYY-MM-DD` (a timestamp suffix is ignored). */
  date: string;
  name: string;
  /** `public` / `company` are days off; `optional` is not. */
  type?: string | null;
  appliesToAllBranches?: boolean;
  branchIds?: string[] | null;
}

/** What the card needs to tell a working day from a day off. */
export interface WorkingDayCalendar {
  /** Days of the week off (0 = Sunday), from the company leave settings. */
  weeklyHolidays: number[];
  holidays: OnLeaveHolidayLike[];
  /**
   * The locations the viewer is looking at, when the server returned
   * holidays for several (HR on "All locations"). A holiday then makes the
   * day off only when it covers every one of them: a Quebec-only holiday is
   * a working day for the Ontario team. Leave it empty when the server
   * already scoped the holidays to one location (a picked location, the
   * employee portal).
   */
  branchIdsInView?: string[];
}

export type NonWorkingDayKind = 'weekend' | 'non_working' | 'holiday';

export interface NonWorkingDay {
  kind: NonWorkingDayKind;
  /** Set when `kind` is `holiday`. */
  holidayName?: string;
}

const OPTIONAL_HOLIDAY_TYPE = 'optional';

/** `YYYY-MM-DD` to a local midnight `Date`, so the weekday never shifts with
 *  the time zone the way `new Date('YYYY-MM-DD')` (UTC) does. */
export function dateFromKey(key: string) {
  const [year, month, day] = key.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function keyFromDate(date: Date) {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function addDaysToKey(key: string, days: number) {
  const date = dateFromKey(key);
  date.setDate(date.getDate() + days);
  return keyFromDate(date);
}

/** The holiday that makes `dateKey` a day off for the locations in view, or
 *  null. Optional holidays never do. */
export function getDayOffHoliday(
  dateKey: string,
  { holidays, branchIdsInView }: WorkingDayCalendar
): OnLeaveHolidayLike | null {
  const key = dateKey.slice(0, 10);
  const onDay = holidays.filter(
    (holiday) =>
      holiday.date.slice(0, 10) === key &&
      holiday.type !== OPTIONAL_HOLIDAY_TYPE
  );
  if (onDay.length === 0) return null;

  const companyWide = onDay.find(
    (holiday) => holiday.appliesToAllBranches !== false
  );
  if (companyWide) return companyWide;
  if (!branchIdsInView?.length) return onDay[0];

  const coversAll = branchIdsInView.every((branchId) =>
    onDay.some((holiday) => holiday.branchIds?.includes(branchId))
  );
  return coversAll ? onDay[0] : null;
}

/**
 * Why `dateKey` is not a working day, or null when it is. A holiday wins
 * (it names the day); then a Saturday or Sunday off is the weekend; any
 * other weekday off in the company schedule is a non-working day.
 */
export function getNonWorkingDay(
  dateKey: string,
  calendar: WorkingDayCalendar
): NonWorkingDay | null {
  const holiday = getDayOffHoliday(dateKey, calendar);
  if (holiday) {
    /* Company-entered, so the casing is whatever was typed ("off Day"):
       lifting the first letter keeps it reading as a name. */
    const name = holiday.name.charAt(0).toUpperCase() + holiday.name.slice(1);
    return { kind: 'holiday', holidayName: name };
  }

  const dayOfWeek = dateFromKey(dateKey).getDay();
  if (!calendar.weeklyHolidays.includes(dayOfWeek)) return null;
  return {
    kind: dayOfWeek === 0 || dayOfWeek === 6 ? 'weekend' : 'non_working',
  };
}

/** The first working day after `dateKey` (weekly offs and day-off holidays
 *  skipped), or null when none falls within `maxScan` days. */
export function findNextWorkingDay(
  dateKey: string,
  calendar: WorkingDayCalendar,
  maxScan = 31
): string | null {
  let cursor = dateKey.slice(0, 10);
  for (let scanned = 0; scanned < maxScan; scanned += 1) {
    cursor = addDaysToKey(cursor, 1);
    if (!getNonWorkingDay(cursor, calendar)) return cursor;
  }
  return null;
}

/** How far past the day strip both cards fetch, so the next working day
 *  after the strip's last day (and the holidays in between) is loaded. */
export const NEXT_WORKING_DAY_LOOKAHEAD_DAYS = 7;

/** Labels for `buildPresenceBuckets`' four buckets — each portal supplies
 *  its own translated copy. */
export interface PresenceBucketLabels {
  present: string;
  hourly: string;
  halfDay: string;
  fullDay: string;
}

/**
 * Builds the presence bar's segments + legend buckets (available / hourly /
 * half-day / full-day), given the day's leave entries and the company's
 * total headcount. Shared by both portals' On Leave cards.
 *
 * The first bucket is availability (headcount minus leave), not measured
 * attendance. On a weekly off or company holiday nobody is expected in, so
 * pass `isWorkingDay: false` and it counts nobody rather than the whole team
 * (the cards hide the bar on those days and show the next working day).
 */
export function buildPresenceBuckets<T>(
  leaves: T[],
  totalEmployeeCount: number,
  getKind: (item: T) => OnLeaveDurationKind,
  labels: PresenceBucketLabels,
  { isWorkingDay = true }: { isWorkingDay?: boolean } = {}
) {
  const counts: Record<OnLeaveDurationKind, number> = {
    [LeaveDuration.HOURLY]: 0,
    [LeaveDuration.HALF_DAY]: 0,
    [LeaveDuration.FULL_DAY]: 0,
  };
  leaves.forEach((leave) => {
    counts[getKind(leave)] += 1;
  });

  const present = isWorkingDay
    ? Math.max(totalEmployeeCount - leaves.length, 0)
    : 0;
  const share = (value: number) =>
    totalEmployeeCount > 0 ? `${(value / totalEmployeeCount) * 100}%` : '0%';

  const buckets = [
    {
      key: 'present',
      label: labels.present,
      count: present,
      color: 'var(--brand-primary)',
    },
    {
      key: LeaveDuration.HOURLY,
      label: labels.hourly,
      count: counts[LeaveDuration.HOURLY],
      color: 'var(--brand-blue-deep)',
    },
    {
      key: LeaveDuration.HALF_DAY,
      label: labels.halfDay,
      count: counts[LeaveDuration.HALF_DAY],
      color: 'var(--brand-amber)',
    },
    {
      key: LeaveDuration.FULL_DAY,
      label: labels.fullDay,
      count: counts[LeaveDuration.FULL_DAY],
      color: 'var(--brand-error)',
    },
  ];

  return {
    present,
    out: leaves.length,
    buckets,
    segments: buckets.map((bucket) => ({
      ...bucket,
      width: share(bucket.count),
    })),
  };
}
